import { useState } from 'react';
import { useAlert } from 'models/alertContext';
import { EAlertVariants } from 'components/atoms/Alert';
import { useTranslations } from 'hooks/useTranslations';
import { ActionTypes } from 'state/actions/alert';
import {
  getGallery,
  getGalleryPreview,
  getGalleryWithLicences,
  updateGallery,
} from 'requests/gallery';
import {
  ESize,
  IEntryData,
  IGaleryFormValues,
  IGaleryRequest,
  IPhoto,
  IPhotoUpload,
} from 'models/galery';

export const useGallery = () => {
  const { t } = useTranslations();
  const [imagesData, setImagesData] = useState<IEntryData[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [galleryId, setGalleryId] = useState<string | null>(null);

  const { setAlert } = useAlert();

  const getGalleryImage = async (id: string, size: ESize) => {
    try {
      return await getGalleryPreview(id, size);
    } catch (e: any) {
      return null;
    }
  };

  const getGalleryThumbnails = async (ids: string[], size: ESize) => {
    try {
      const results = await Promise.all(
        ids.map(async (id) => {
          const image = await getGalleryImage(id, size);
          return image ? { id, image } : null;
        }),
      );

      return results.reduce((acc, item) => {
        if (item) {
          acc[item.id] = item.image;
        }
        return acc;
      }, {} as IPhoto);
    } catch (e: any) {
      return null;
    }
  };

  const getGalleryData = async (
    ownerObjectId: string,
    withPhoto: boolean = false,
    withLinkedLicences = false,
  ) => {
    try {
      const data = withLinkedLicences
        ? await getGalleryWithLicences(ownerObjectId)
        : await getGallery(ownerObjectId, withPhoto);
      if (data) {
        const { entries, version, uniqueId } = data;
        setImagesData(entries.sort((a, b) => Number(a.position) - Number(b.position)));
        setVersion(version);
        setGalleryId(uniqueId);
      } else {
        setImagesData([]);
        setVersion(null);
        setGalleryId(null);
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response.data?.message,
        variant: EAlertVariants.error,
      });
    }
  };

  const handleSaveGalleryImage = async (form: IGaleryFormValues, files: IPhotoUpload) => {
    if (!galleryId) return null;
    try {
      const parameters: IGaleryRequest = {
        entryIdsForRemoval: form.entryIdsForRemoval,
        entryParameters: form.entryParameters.map(({ uniqueId, position, filename }) => ({
          uniqueId,
          position,
          filename,
        })),
        version: version ?? 0,
      };

      await updateGallery(galleryId, parameters, files);
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response.data.message,
        variant: EAlertVariants.error,
      });
      return null;
    }
  };

  return {
    getGalleryData,
    handleSaveGalleryImage,
    imagesData,
  };
};
